import React, { FunctionComponent, useRef } from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { StyledResults } from './styledComponents';
import { useQueryParams } from 'use-query-params';
import {
  AppQueryParamsConfigMap,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from '../../app/constants';
import { useEventResults } from '../../api/useEventResults';
import { Helmet } from 'react-helmet';
import { APP_NAME_POSTFIX } from '../../constants';
import EmptyPageComponent from '../../components/EmptyPage/EmptyPage.component';
import getResultsItem from './getResultsItem';
import getResultsTableHead from './getResultsTableHead';
import { useEventFeed } from '../../api/useEventFeed';
import SplashScreenComponent from '../../components/SplashScreen/SplashScreen.component';
import { LiveFeedEventResult } from '../../domain';
import DownloadResults from '../../components/DownloadResults/DownloadResults.component';
import useDidUpdateEffect from '../../hooks/useDidUpdate';
import useWindowSize from '../../hooks/useWindowSize';
import { Disciplines } from '../../domain';
import useResultsSort from './hooks/useResultsSort';
import { useScrollOnSearch } from '../../hooks/useScrollOnSearch';
import HorizontalScrollPromptComponent from '../../components/HorizontalScrollPrompt/HorizontalScrollPrompt.component';

const connector = connect(
  (state: RootState) => ({
    searchQuery: state.app.searchQuery['results'],
  }),
  {},
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Results: FunctionComponent<PropsFromRedux> = ({ searchQuery }) => {
  const [{ c: classId, e: eventId }] = useQueryParams(AppQueryParamsConfigMap);

  const { data: activeEvent, dataUpdatedAt: eventNewsFeedUpdatedAt } =
    useEventFeed({ eventId });

  const { sort, changeSort } = useResultsSort();

  const {
    data: results,
    isFetching,
    refetch: refetchResults,
  } = useEventResults(eventId, classId, searchQuery, sort);

  useDidUpdateEffect(() => {
    // Event refreshes with a given time interval so we need
    // refetch results when event is refreshed
    refetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventNewsFeedUpdatedAt]);

  const { width = 0 } = useWindowSize();

  const desktopVisible = width > TABLET_BREAKPOINT;
  const tabletVisible = width <= TABLET_BREAKPOINT && width > MOBILE_BREAKPOINT;
  const mobileVisible = width <= MOBILE_BREAKPOINT;

  const containerRef = useRef<HTMLDivElement>(null);
  useScrollOnSearch(containerRef, 600, -120);

  const renderTitle = () => (
    <Helmet>
      <title>
        {activeEvent?.eventName} Results - {APP_NAME_POSTFIX}
      </title>
    </Helmet>
  );

  if (activeEvent?.resultsDocUrl && !results?.length && !isFetching) {
    return <DownloadResults url={activeEvent.resultsDocUrl} />;
  }

  if (isFetching && !results) {
    return <SplashScreenComponent />;
  }

  if (!results?.length && searchQuery) {
    return (
      <div className="mt-20">
        {renderTitle()}
        <EmptyPageComponent title="Nothing found">
          Try entering just part Rider or Horse Name
        </EmptyPageComponent>
      </div>
    );
  }

  const activeClass = classId
    ? activeEvent?.eventClassList.find((c) => c.id === +classId)
    : undefined;

  if (
    !results?.length ||
    !activeEvent?.publishResultsLiveFeed ||
    activeClass?.linkAvailable === false
  ) {
    return (
      <div className="mt-20">
        <EmptyPageComponent title="No results">
          No Results currently published
        </EmptyPageComponent>
      </div>
    );
  }

  const ResultsItem = getResultsItem(activeEvent?.discipline);
  const ResultsTableHead = getResultsTableHead(activeEvent?.discipline);

  let judges = activeClass?.judgeNamesPositions || [];

  let sjPhasesCount = activeClass?.sjPhasesCount || 0;

  // const hasBridleNumber = results.some((result) => result.bridleNumber);

  const disabledFields = results.length
    ? Object.keys(results[0]).filter(
        (key) =>
          !results.some(
            (result) =>
              result[key as keyof LiveFeedEventResult] &&
              result[key as keyof LiveFeedEventResult] !== 'N/A',
          ),
      )
    : [];

  if (
    activeEvent.discipline === Disciplines.Showjumping &&
    !activeEvent.showSJFullResults
  ) {
    sjPhasesCount = 0;
    disabledFields.push('totalJFaults');
  }

  return (
    <StyledResults className="mt-20 overflow-y-auto" ref={containerRef}>
      {renderTitle()}
      <HorizontalScrollPromptComponent />
      <table className="table sticky-head">
        <ResultsTableHead
          youngHorseResultsRule={activeClass?.youngHorseResultsRule}
          judges={judges}
          disabledFields={disabledFields}
          changeSort={changeSort}
          sort={sort}
          sjPhasesCount={sjPhasesCount}
          desktopVisible={desktopVisible}
          mobileVisible={mobileVisible}
          tabletVisible={tabletVisible}
          scoreByOptimumTime={activeEvent?.scoreByOptimumTime}
        />
        <tbody>
          {results?.map((item) => (
            <ResultsItem
              result={item}
              key={item.secretaryEventId}
              judges={judges}
              type={activeEvent?.evStatus}
              sjPhasesCount={sjPhasesCount}
              disabledFields={disabledFields}
              desktopVisible={desktopVisible}
              mobileVisible={mobileVisible}
              tabletVisible={tabletVisible}
              scoreByOptimumTime={activeEvent?.scoreByOptimumTime}
              isScoringEntered={item.isScoringEntered}
            />
          ))}
        </tbody>
      </table>
    </StyledResults>
  );
};

export default connector(Results);
